import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, ImageOff, X } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const MAX_IMAGE_BYTES = 1024 * 1024;
const FIELD_LABEL = "text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block";
const INPUT_CLS = "h-10 bg-white border-black/10";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImageField({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be under 1 MB.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      onChange(dataUrl);
    } catch {
      toast.error("Could not read the selected file.");
    } finally {
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-16 h-16 rounded-md border border-black/10 bg-[#FAFAFA] flex items-center justify-center overflow-hidden shrink-0">
        {value ? (
          <img src={value} alt="" className="w-full h-full object-contain" />
        ) : (
          <ImageOff className="h-5 w-5 text-[#A3A3A3]" />
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid={testid}
        />
        <Button
          type="button"
          variant="outline"
          className="border-black/10 h-9"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-1.5" />
          {value ? "Replace" : "Choose file"}
        </Button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => onChange("")}
            className="text-[#525252] hover:text-[#F50E02] h-9"
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// Year dropdown — historical years (current + 5 back to current - 60) by default,
// but accepts an explicit `maxYear` to extend up to 2099 for fields like expiry.
function YearSelect({ value, onChange, placeholder, testid, maxYear }) {
  const current = new Date().getFullYear();
  const top = maxYear || current + 5;
  const bottom = current - 60;
  const years = [];
  for (let y = top; y >= bottom; y -= 1) years.push(String(y));
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className={INPUT_CLS} data-testid={testid}>
        <SelectValue placeholder={placeholder || "Year"} />
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {years.map((y) => (
          <SelectItem key={y} value={y}>
            {y}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const BLANK = {
  cylinder_serial_number: "",
  prefix_id: "",
  cylinder_manufacturer_name: "",
  cylinder_image: "",
  cylinder_manufacturer_year: "",
  expiry_year: "",
  last_test_date: "",
  due_test_date: "",
  cylinder_capacity_id: "",
};

export default function CylinderFormDialog({
  open,
  onOpenChange,
  fuelAccessId,
  fuelAccessPrefixId,
  fuelAccessPrefixName,
  cylinder,
  onSaved,
}) {
  const editing = !!cylinder;
  const [form, setForm] = useState(BLANK);
  const [prefixes, setPrefixes] = useState([]);
  const [capacities, setCapacities] = useState([]);
  const [testYears, setTestYears] = useState([]);
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load masters once on first open
  useEffect(() => {
    if (!open || mastersLoaded) return;
    (async () => {
      try {
        const [p, c, ty] = await Promise.all([
          api.get("/master/fuel_access_prefixes"),
          api.get("/master/cylinder_capacities"),
          api.get("/master/test_years"),
        ]);
        setPrefixes(p.data || []);
        setCapacities(c.data || []);
        setTestYears(ty.data || []);
        setMastersLoaded(true);
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) ||
            "Failed to load master data"
        );
      }
    })();
  }, [open, mastersLoaded]);

  // Reset form when opening
  useEffect(() => {
    if (!open) return;
    if (cylinder) {
      setForm({
        cylinder_serial_number: cylinder.cylinder_serial_number || "",
        prefix_id:
          fuelAccessPrefixId || cylinder.prefix_id || "",
        cylinder_manufacturer_name: cylinder.cylinder_manufacturer_name || "",
        cylinder_image: cylinder.cylinder_image || "",
        cylinder_manufacturer_year: cylinder.cylinder_manufacturer_year || "",
        expiry_year: cylinder.expiry_year || "",
        last_test_date: cylinder.last_test_date || "",
        test_years_id: cylinder.test_years_id || "",
        due_test_date: cylinder.due_test_date || "",
        cylinder_capacity_id: cylinder.cylinder_capacity_id || "",
      });
    } else {
      setForm({ ...BLANK, prefix_id: fuelAccessPrefixId || "" });
    }
  }, [open, cylinder, fuelAccessPrefixId]);

  // Auto-fill Due Test Date = Last Test Date + Test Years (when both set)
  useEffect(() => {
    if (!form.last_test_date || !form.test_years_id || testYears.length === 0) return;
    const ty = testYears.find((t) => t.id === form.test_years_id);
    const n = parseInt((ty?.name || "").trim(), 10);
    if (!Number.isFinite(n) || n <= 0) return;
    const d = new Date(form.last_test_date);
    if (Number.isNaN(d.getTime())) return;
    d.setFullYear(d.getFullYear() + n);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const next = `${yyyy}-${mm}-${dd}`;
    setForm((prev) =>
      prev.due_test_date === next ? prev : { ...prev, due_test_date: next }
    );
  }, [form.last_test_date, form.test_years_id, testYears]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const isValid = () => {
    for (const k of Object.keys(BLANK)) {
      if (k === "cylinder_image") continue; // optional
      const v = form[k];
      if (!v || (typeof v === "string" && !v.trim())) return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!isValid()) {
      toast.error("Please fill all fields.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/cylinders/${cylinder.id}`, form);
        toast.success("Cylinder updated");
        onSaved?.(data);
      } else {
        const { data } = await api.post(
          `/fuel-access/${fuelAccessId}/cylinders`,
          form
        );
        toast.success("Cylinder added");
        onSaved?.(data);
      }
      onOpenChange?.(false);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedPrefixName =
    prefixes.find((p) => p.id === form.prefix_id)?.name || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit Cylinder" : "Add Cylinder"}
          </DialogTitle>
          <DialogDescription>
            All fields are mandatory. The Fuel Access Sticker Value is the prefix
            combined with the cylinder serial number
            {selectedPrefixName && form.cylinder_serial_number ? (
              <>
                {" "}
                — preview: <b>{selectedPrefixName}{form.cylinder_serial_number}</b>
              </>
            ) : null}
            .
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className={FIELD_LABEL}>
              Cylinder Serial Number <span className="text-[#F50E02]">*</span>
            </Label>
            <Input
              value={form.cylinder_serial_number}
              onChange={(e) =>
                setField("cylinder_serial_number", e.target.value)
              }
              placeholder="Enter Cylinder Serial Number"
              className={INPUT_CLS}
              data-testid="cylinder-serial-input"
            />
          </div>

          <div>
            <Label className={FIELD_LABEL}>
              Fuel Access Sticker Value Prefix{" "}
              <span className="text-[#F50E02]">*</span>
            </Label>
            <Input
              value={
                fuelAccessPrefixName ||
                prefixes.find((p) => p.id === form.prefix_id)?.name ||
                ""
              }
              readOnly
              disabled
              placeholder="Prefix from fuel access"
              className="h-10 bg-[#FAFAFA] border-black/10 text-[#0A0A0A] font-medium cursor-not-allowed disabled:opacity-100"
              data-testid="cylinder-prefix-readonly"
            />
          </div>

          <div>
            <Label className={FIELD_LABEL}>
              Cylinder Manufacturer Name <span className="text-[#F50E02]">*</span>
            </Label>
            <Input
              value={form.cylinder_manufacturer_name}
              onChange={(e) =>
                setField("cylinder_manufacturer_name", e.target.value)
              }
              placeholder="Enter Cylinder Manufacturer Name"
              className={INPUT_CLS}
              data-testid="cylinder-mfr-name-input"
            />
          </div>

          <div>
            <Label className={FIELD_LABEL}>
              Cylinder Capacity <span className="text-[#F50E02]">*</span>
            </Label>
            <Select
              value={form.cylinder_capacity_id || ""}
              onValueChange={(v) => setField("cylinder_capacity_id", v)}
            >
              <SelectTrigger className={INPUT_CLS} data-testid="cylinder-capacity-select">
                <SelectValue placeholder="Choose capacity" />
              </SelectTrigger>
              <SelectContent>
                {capacities.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-[#525252]">
                    No capacities. Add some in Master Data → Cylinder Capacity.
                  </div>
                ) : (
                  capacities.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className={FIELD_LABEL}>
              Cylinder Manufacturer Year <span className="text-[#F50E02]">*</span>
            </Label>
            <YearSelect
              value={form.cylinder_manufacturer_year}
              onChange={(v) => setField("cylinder_manufacturer_year", v)}
              placeholder="Year"
              testid="cylinder-mfr-year-select"
            />
          </div>

          <div>
            <Label className={FIELD_LABEL}>
              Expiry Year <span className="text-[#F50E02]">*</span>
            </Label>
            <YearSelect
              value={form.expiry_year}
              onChange={(v) => setField("expiry_year", v)}
              placeholder="Enter Expiry Year"
              testid="cylinder-expiry-year-select"
              maxYear={2099}
            />
          </div>

          <div>
            <Label className={FIELD_LABEL}>
              Last Test Date <span className="text-[#F50E02]">*</span>
            </Label>
            <Input
              type="date"
              value={form.last_test_date}
              onChange={(e) => setField("last_test_date", e.target.value)}
              className={INPUT_CLS}
              data-testid="cylinder-last-test-date-input"
            />
          </div>

          <div>
            <Label className={FIELD_LABEL}>
              Test Years <span className="text-[#F50E02]">*</span>
            </Label>
            <Select
              value={form.test_years_id || ""}
              onValueChange={(v) => setField("test_years_id", v)}
            >
              <SelectTrigger className={INPUT_CLS} data-testid="cylinder-test-years-select">
                <SelectValue placeholder="Choose test years" />
              </SelectTrigger>
              <SelectContent>
                {testYears.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-[#525252]">
                    No test years. Add some in Master Data → Test Years.
                  </div>
                ) : (
                  testYears.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className={FIELD_LABEL}>
              Due Test Date <span className="text-[#F50E02]">*</span>
            </Label>
            <Input
              type="date"
              value={form.due_test_date}
              readOnly
              tabIndex={-1}
              className="h-10 bg-[#FAFAFA] border-black/10 text-[#0A0A0A] cursor-not-allowed"
              data-testid="cylinder-due-test-date-input"
            />
            <p className="text-xs text-[#A3A3A3] mt-1">
              Auto-calculated from Last Test Date + Test Years.
            </p>
          </div>

          <div className="sm:col-span-2">
            <Label className={FIELD_LABEL}>Cylinder Image</Label>
            <ImageField
              value={form.cylinder_image}
              onChange={(v) => setField("cylinder_image", v)}
              testid="cylinder-image-input"
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            className="border-black/10"
            data-testid="cylinder-form-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !isValid()}
            className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
            data-testid="cylinder-form-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {editing ? "Update Cylinder" : "Add Cylinder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
